import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FaDownload, FaTimes, FaFileAlt, FaSpinner } from "react-icons/fa";
import API from "../../lib/apiUtils";

const DownloadPopup = ({ isOpen, onClose, documentName, documentUrl }) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: "",
    company: "",
    email: "",
    role: "",
    privacyAccepted: false,
    marketingConsent: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [settings, setSettings] = useState(null);

  // Fetch plugin settings once on mount
  useEffect(() => {
    API.get("/download-leads/settings")
      .then((data) => setSettings(data))
      .catch(() => {
        // Fallback defaults if settings endpoint is unavailable
        setSettings({
          download_behavior: "direct",
          privacy_policy_url: "/privacy-policy",
          admin_notifications: false,
          ga_gtm_event: false,
        });
      });
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = true;
    if (!formData.company.trim()) newErrors.company = true;
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = true;
    if (!formData.role) newErrors.role = true;
    if (!formData.privacyAccepted) newErrors.privacyAccepted = true;
    return newErrors;
  };

  const triggerDownload = useCallback(() => {
    const behavior = settings?.download_behavior || "direct";

    if (behavior === "redirect") {
      // Option B — open PDF URL in a new tab
      window.open(documentUrl, "_blank", "noopener,noreferrer");
    } else {
      // Option A (default) — force file download
      const link = document.createElement("a");
      link.href = documentUrl;
      link.download = documentName;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [settings, documentUrl, documentName]);

  const pushGTMEvent = useCallback(() => {
    if (!settings?.ga_gtm_event) return;
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "catalog_download",
        document_name: documentName,
        user_role: formData.role,
      });
    }
  }, [settings, documentName, formData.role]);

  const resetForm = () => {
    setFormData({
      fullName: "",
      company: "",
      email: "",
      role: "",
      privacyAccepted: false,
      marketingConsent: false,
    });
    setErrors({});
    setApiError("");
    setIsSubmitting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setApiError("");

    try {
      await API.post("/download-leads", {
        full_name: formData.fullName,
        company: formData.company,
        email: formData.email,
        role: formData.role,
        document_name: documentName,
        document_url: documentUrl,
        privacy_accepted: formData.privacyAccepted,
        marketing_consent: formData.marketingConsent,
        language: i18n.language.includes("en") ? "en" : "it",
        website: "", // honeypot
      });

      pushGTMEvent();
      triggerDownload();
      resetForm();
      onClose();
    } catch (err) {
      const message = err?.message || t("download_popup.error_generic");
      setApiError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const privacyUrl = settings?.privacy_policy_url || "/privacy-policy";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#0a1e3d]/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-8 animate-fade-in">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
        >
          <FaTimes size={18} />
        </button>

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
            <FaFileAlt className="text-main-100" size={20} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {t("download_popup.title")}
            </h3>
            <p className="text-sm text-gray-500">
              {t("download_popup.subtitle")}
            </p>
          </div>
        </div>

        {/* Document name */}
        <div className="bg-gray-50 border-l-4 border-main-100 rounded-lg px-4 py-3 mb-6 flex items-center gap-3">
          <FaFileAlt className="text-gray-400" size={16} />
          <span className="text-sm font-medium text-gray-700">{documentName}</span>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t("download_popup.full_name")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-main-100 transition ${
                errors.fullName ? "border-red-400" : "border-gray-300"
              }`}
              placeholder={t("download_popup.full_name")}
            />
          </div>

          {/* Company & Email row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {t("download_popup.company")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-main-100 transition ${
                  errors.company ? "border-red-400" : "border-gray-300"
                }`}
                placeholder={t("download_popup.company")}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-main-100 transition ${
                  errors.email ? "border-red-400" : "border-gray-300"
                }`}
                placeholder="Email"
              />
            </div>
          </div>

          {/* Role select */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t("download_popup.role")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-main-100 transition bg-white appearance-none ${
                errors.role ? "border-red-400" : "border-gray-300"
              } ${!formData.role ? "text-gray-400" : "text-gray-900"}`}
            >
              <option value="" disabled>
                {t("download_popup.select_role")}
              </option>
              <option value="cliente_finale">{t("download_popup.role_final_customer")}</option>
              <option value="rivenditore">{t("download_popup.role_reseller")}</option>
              <option value="installatore">{t("download_popup.role_installer")}</option>
              <option value="progettista">{t("download_popup.role_designer")}</option>
              <option value="distributore">{t("download_popup.role_distributor")}</option>
              <option value="impresa_edile">{t("download_popup.role_construction")}</option>
              <option value="agente">{t("download_popup.role_agent")}</option>
              <option value="altro">{t("download_popup.role_other")}</option>
            </select>
          </div>

          {/* Privacy checkbox */}
          <div className="space-y-3 pt-2">
            <label className={`flex items-start gap-3 cursor-pointer ${errors.privacyAccepted ? "text-red-500" : ""}`}>
              <input
                type="checkbox"
                name="privacyAccepted"
                checked={formData.privacyAccepted}
                onChange={handleChange}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-main-100 focus:ring-main-100"
              />
              <span className="text-sm text-gray-600">
                {t("download_popup.privacy_text")}{" "}
                <a
                  href={privacyUrl}
                  className="text-main-100 underline font-medium"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("download_popup.privacy_link")}
                </a>{" "}
                <span className="text-red-500">*</span>
              </span>
            </label>

            {/* Marketing checkbox */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="marketingConsent"
                checked={formData.marketingConsent}
                onChange={handleChange}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-main-100 focus:ring-main-100"
              />
              <span className="text-sm text-gray-600">
                {t("download_popup.marketing_text")}
              </span>
            </label>
          </div>

          {/* API Error */}
          {apiError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
              {apiError}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-4">
            <p className="text-xs text-red-500 font-medium">
              * {t("download_popup.required_fields")}
            </p>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 bg-main-100 hover:bg-opacity-90 text-white px-6 py-2.5 rounded-lg font-medium text-sm transition shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <FaSpinner size={14} className="animate-spin" />
              ) : (
                <FaDownload size={14} />
              )}
              {isSubmitting
                ? t("download_popup.sending")
                : t("download_popup.download_button")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DownloadPopup;
